import type { Article } from '@/lib/content-types';
import { articleBodyPlainText } from '@/lib/article-body';
import {
    articleOgImageUrl,
    breadcrumbJsonLd,
    OG_IMAGE_HEIGHT,
    OG_IMAGE_WIDTH,
    publisherLogoUrl,
} from '@/lib/seo';

import { absoluteUrl, SITE_NAME, siteUrl } from '@/lib/site-url';

/**
 * Google News / rich-results friendly `NewsArticle` payload for the article view,
 * plus a matching `BreadcrumbList`.
 */
export function buildNewsArticleJsonLd(
    article: Article,
): Record<string, unknown>[] {
    const pageUrl =
        article.canonical?.trim()
        || absoluteUrl(`/article/${encodeURIComponent(article.slug)}`);
    const origin = siteUrl();
    const imageUrl = articleOgImageUrl(article.coverOgUrl);

    const authors = article.authors
        .filter((a) => a.name.trim() !== '')
        .map((a) => {
            const person: Record<string, unknown> = {
                '@type': 'Person',
                name: a.name,
                url: absoluteUrl(`/author/${encodeURIComponent(a.$id)}`),
            };
            if (a.profileUrl) person.image = a.profileUrl;
            return person;
        });

    const keywords = article.categories.map((c) => c.name).filter(Boolean);
    const primarySection = article.categories[0];
    const wordCount = articleBodyPlainText(article.body).split(/\s+/).filter(Boolean).length;

    const articlePayload: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        '@id': `${pageUrl}#article`,
        isAccessibleForFree: true,
        inLanguage: 'he-IL',
        headline: article.title,
        description:
            article.subtitle
            || articleBodyPlainText(article.body).slice(0, 300),
        datePublished: article.date,
        dateModified: article.$updatedAt,
        url: pageUrl,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': pageUrl,
        },
        isPartOf: { '@id': `${origin}/#website` },
        publisher: {
            '@type': 'NewsMediaOrganization',
            '@id': `${origin}/#organization`,
            name: SITE_NAME,
            url: origin,
            logo: {
                '@type': 'ImageObject',
                url: publisherLogoUrl(),
                width: 512,
                height: 512,
            },
        },
        image: {
            '@type': 'ImageObject',
            url: imageUrl,
            width: OG_IMAGE_WIDTH,
            height: OG_IMAGE_HEIGHT,
        },
        speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['h1', 'article p'],
        },
    };

    if (authors.length > 0) articlePayload.author = authors;
    if (primarySection?.name) articlePayload.articleSection = primarySection.name;
    if (keywords.length > 0) articlePayload.keywords = keywords.join(', ');
    if (wordCount > 0) articlePayload.wordCount = wordCount;

    const crumbs = [
        { name: 'ראשי', path: '/' },
        ...(primarySection
            ? [{ name: primarySection.name, path: `/category/${encodeURIComponent(primarySection.key)}` }]
            : []),
        { name: article.title, path: `/article/${encodeURIComponent(article.slug)}` },
    ];

    return [articlePayload, breadcrumbJsonLd(crumbs)];
}
